import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MarketplaceProduct } from '@/types';
import { Badge } from '@/components/ui';
import { ShoppingCart, Wrench, Package } from 'lucide-react';
import { formatPrice, Stars } from './format';

interface ProductCardProps {
  product: MarketplaceProduct;
  /** Base path for the product link (e.g. "/industry/marketplace"). */
  linkBase: string;
  onAddToCart?: (product: MarketplaceProduct) => void;
}

export function ProductCard({ product, linkBase, onAddToCart }: ProductCardProps) {
  const [imgError, setImgError] = useState(false);
  const image = product.images[0];
  const lowStock = product.type === 'product' && product.stock != null && product.stock <= 5;

  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-900">
      <Link to={`${linkBase}/product/${product.id}`} className="block">
        <div className="relative aspect-[3/2] w-full overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-950 dark:to-indigo-950">
          {image && !imgError ? (
            <img src={image} alt={product.name} onError={() => setImgError(true)} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-3xl font-black text-blue-300 dark:text-blue-700">{product.name.charAt(0)}</div>
          )}
          <div className="absolute left-2 top-2 flex gap-1.5">
            <Badge tone={product.type === 'service' ? 'purple' : 'blue'}>{product.type}</Badge>
            {lowStock && <Badge tone="amber">Low stock</Badge>}
          </div>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <p className="text-xs text-gray-400 dark:text-gray-500">{product.companyName}</p>
        <Link to={`${linkBase}/product/${product.id}`} className="mt-0.5 line-clamp-1 font-bold text-gray-900 hover:text-blue-600 dark:text-gray-100 dark:hover:text-blue-400">
          {product.name}
        </Link>
        <p className="mt-1 line-clamp-2 flex-1 text-sm text-gray-500 dark:text-gray-400">{product.description}</p>

        <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Stars value={product.stats.averageRating} />
          <span>{product.stats.averageRating.toFixed(1)}</span>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          <span>{product.stats.reviewsCount} reviews</span>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div>
            <span className="font-bold text-gray-900 dark:text-gray-100">{formatPrice(product.price, 'usd')}</span>
            {product.tokenPrice != null && (
              <span className="ml-1 text-xs text-blue-600 dark:text-blue-400">or {product.tokenPrice} tokens</span>
            )}
          </div>
          {onAddToCart && (
            <button
              onClick={() => onAddToCart(product)}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
            >
              <ShoppingCart className="h-3.5 w-3.5" /> Add
            </button>
          )}
        </div>

        {product.type === 'service' && product.deliveryTime && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500"><Wrench className="h-3 w-3" /> {product.deliveryTime}</p>
        )}
        {product.type === 'product' && product.isDigital && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500"><Package className="h-3 w-3" /> Digital delivery</p>
        )}
      </div>
    </div>
  );
}
